// components/LoomPlayer.tsx
'use client';

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Play, Loader2 } from 'lucide-react';

export default function LoomPlayer({ isOpen, onClose, loomUrl, title }) {
  const [isLoading, setIsLoading] = useState(true);
  const [embedUrl, setEmbedUrl] = useState('');

  useEffect(() => {
    if (isOpen && loomUrl) {
      // Convert Loom share URL to embed URL
      const videoId = extractLoomVideoId(loomUrl);
      if (videoId) {
        setEmbedUrl(`https://www.loom.com/embed/${videoId}`);
      }
      setIsLoading(true);
    }
  }, [isOpen, loomUrl]);

  // Extract video ID from various Loom URL formats
  const extractLoomVideoId = (url) => {
    const patterns = [
      /loom\.com\/share\/([a-zA-Z0-9-]+)/,
      /loom\.com\/embed\/([a-zA-Z0-9-]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }

    if (/^[a-zA-Z0-9-]+$/.test(url)) {
      return url;
    }

    return null;
  };

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Backdrop */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
        }}
        onClick={onClose}
      />

      {/* Fullscreen Container */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            flexShrink: 0,
          }}
        >
          {title && (
            <h3 className="text-white font-semibold text-lg truncate pr-4">
              {title}
            </h3>
          )}
          <button
            onClick={onClose}
            className="ml-auto p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Video Container - fills remaining space */}
        <div
          style={{
            position: 'relative',
            flex: 1,
            backgroundColor: '#000',
            overflow: 'hidden',
          }}
        >
          {/* Loading State */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
              <Loader2 className="w-10 h-10 text-white animate-spin" />
            </div>
          )}

          {/* Iframe */}
          {embedUrl ? (
            <iframe
              src={embedUrl}
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              onLoad={() => setIsLoading(false)}
              allow="autoplay; fullscreen"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-white">
              <div className="text-center">
                <p className="text-red-400 font-medium mb-2">Invalid Loom URL</p>
                <p className="text-sm text-gray-400">Please provide a valid Loom share URL</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <p
          style={{ padding: '12px 0', textAlign: 'center', flexShrink: 0 }}
          className="text-sm text-gray-400"
        >
          Press <kbd className="px-2 py-1 bg-white/10 rounded text-xs">ESC</kbd> to close
        </p>
      </div>
    </div>,
    document.body
  );
}
